import React, { useState } from 'react';
import { MapPin, Calendar as CalIcon, Clock, ArrowRight, ArrowLeftRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { toast } from '../hooks/use-toast';
import PlaceInput from './PlaceInput';
import { minDate, maxDate, validatePickup, defaultSlot } from '../lib/bookingTime';
import { AIRPORTS } from '../lib/airports';

const TabBtn = ({ active, onClick, icon: Icon, children }) => (
  <button onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${active ? 'bg-[color:var(--brand-dark)] text-white' : 'text-[color:var(--brand-dark)] hover:bg-[color:var(--brand-muted)]'}`}>
    <Icon className="w-4 h-4" /> {children}
  </button>
);

const Field = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[color:var(--brand-dark)] transition-colors bg-white">
    <Icon className="w-4 h-4 text-gray-400 shrink-0" />
    {children}
  </div>
);

const BookingForm = () => {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState('oneway');
  const [form, setForm] = useState(() => { const s = defaultSlot(); return { from: AIRPORTS[0].value, to: '', date: s.date, time: s.time, pax: 2, ret: false, retDate: '', retTime: '12:00', hours: 3 }; });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const incomplete = { de: 'Bitte füllen Sie die Pflichtfelder aus', en: 'Please complete the required fields', fr: 'Veuillez remplir les champs requis', it: 'Compila i campi obbligatori' }[lang];

  const openBooking = () => {
    if (!form.from || (mode === 'oneway' && !form.to) || !form.date) { toast({ title: incomplete }); return; }
    if (mode === 'oneway' && form.ret && !form.retDate) { toast({ title: incomplete }); return; }
    const err = validatePickup(form.date, form.time, lang);
    if (err) { toast({ title: err }); return; }
    const q = new URLSearchParams({
      from: form.from, to: form.to, date: form.date, time: form.time,
      pax: String(form.pax), mode, hours: String(form.hours || 3), ret: String(form.ret),
      retDate: form.ret ? form.retDate : '', retTime: form.ret ? form.retTime : '',
    }).toString();
    navigate(`/booking?${q}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 w-full max-w-md">
      <div className="flex items-center gap-2 mb-5">
        <TabBtn active={mode === 'oneway'} onClick={() => setMode('oneway')} icon={ArrowLeftRight}>{t('hero.oneway')}</TabBtn>
        <TabBtn active={mode === 'hourly'} onClick={() => setMode('hourly')} icon={Clock}>{t('hero.hourly')}</TabBtn>
      </div>
      <div className="space-y-3">
        <PlaceInput icon={MapPin} value={form.from} onChange={(v) => set('from', v)} placeholder={t('hero.from')} testId="hero-from" />
        {mode === 'oneway' && (
          <PlaceInput icon={MapPin} value={form.to} onChange={(v) => set('to', v)} placeholder={t('hero.to')} testId="hero-to" />
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field icon={CalIcon}>
            <input type="date" min={minDate()} max={maxDate()} value={form.date} onChange={(e) => set('date', e.target.value)} className="w-full outline-none text-sm bg-transparent" data-testid="hero-date" />
          </Field>
          <Field icon={Clock}>
            <input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} className="w-full outline-none text-sm bg-transparent" />
          </Field>
        </div>
        {mode === 'hourly' ? (
          <Field icon={Clock}>
            <input type="number" min={1} value={form.hours} onChange={(e) => set('hours', e.target.value)} className="w-full outline-none text-sm bg-transparent" />
            <span className="text-xs text-gray-400">{t('hero.duration')}</span>
          </Field>
        ) : (
          <label className="flex items-center gap-2 text-sm font-medium text-[color:var(--brand-dark)] cursor-pointer select-none">
            <input type="checkbox" checked={form.ret} onChange={(e) => set('ret', e.target.checked)} className="w-4 h-4 accent-[color:var(--brand-dark)]" data-testid="hero-return-toggle" />
            {t('hero.returnTrip')}
          </label>
        )}
        {mode === 'oneway' && form.ret && (
          <div className="grid grid-cols-2 gap-3" data-testid="hero-return-fields">
            <Field icon={CalIcon}>
              <input type="date" value={form.retDate} onChange={(e) => set('retDate', e.target.value)} className="w-full outline-none text-sm bg-transparent" data-testid="hero-return-date" />
            </Field>
            <Field icon={Clock}>
              <input type="time" value={form.retTime} onChange={(e) => set('retTime', e.target.value)} className="w-full outline-none text-sm bg-transparent" data-testid="hero-return-time" />
            </Field>
          </div>
        )}
        <button onClick={openBooking} className="accent-btn w-full rounded-xl py-3 font-bold flex items-center justify-center gap-2">
          {t('hero.next')} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Hero = () => {
  const { t, lang } = useApp();
  const badge = { en: 'Fixed prices · No hidden fees · Zurich', de: 'Festpreise · Keine versteckten Gebühren · Zürich', fr: 'Prix fixes · Sans frais cachés · Zurich', it: 'Prezzi fissi · Nessun costo nascosto · Zurigo' }[lang];
  const stats = [
    ['4.8/5', 'Trustpilot'],
    ['6000+', { en: 'Happy travellers', de: 'Zufriedene Reisende', fr: 'Voyageurs satisfaits', it: 'Viaggiatori felici' }[lang]],
    ['24/7', { en: 'Support', de: 'Support', fr: 'Assistance', it: 'Assistenza' }[lang]],
  ];
  return (
    <section id="book" className="relative min-h-[760px] lg:min-h-[720px] flex items-center brand-gradient overflow-hidden">
      <div className="absolute inset-0">
        <img src="/photos/p2.webp" alt="Premium airport transfer chauffeur Zurich" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(12,44,72,.94) 0%, rgba(18,62,99,.85) 55%, rgba(27,143,201,.55) 100%)' }} />
      </div>
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 15% 25%, rgba(242,101,34,.35), transparent 40%)' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-32 pb-16 grid lg:grid-cols-2 gap-10 items-center w-full">
        <div className="text-white animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-1.5 text-xs font-semibold mb-6">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--brand-accent)' }} /> {badge}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            {t('hero.title1')} <br />
            <span style={{ color: 'var(--brand-accent)' }}>{t('hero.title2')}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/80 max-w-md">{t('hero.subtitle')}</p>
          <div className="mt-8 flex flex-wrap gap-6">
            {stats.map(([a, b]) => (
              <div key={b}>
                <div className="text-2xl font-extrabold" style={{ color: 'var(--brand-accent)' }}>{a}</div>
                <div className="text-xs text-white/70">{b}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex lg:justify-end animate-fade-up">
          <BookingForm />
        </div>
      </div>
    </section>
  );
};

export default Hero;
